/**
 * Breaks sign-in loops between the app and Azure AD B2C.
 *
 * When MSAL discards a returning sign-in (for example after a logout, when B2C's `?state=` query echo
 * was mistaken for a sign-in response, see logout-state-cleanup.ts), the guard and interceptor start
 * the next one, B2C answers from its own session within seconds, and the app bounces every ~3 seconds.
 * Every round trip runs the MFA step on the B2C side, and after a handful of them the MFA service
 * throttles the user for hours ("Phone verification request is throttled").
 *
 * This hook counts sign-in redirects (authorize requests, not logouts) in localStorage, refuses the
 * fourth one within a minute, and forgets them once a redirect completes with a token. Legitimate flows
 * need at most three per minute: sign-in, "forgot password" (AADB2C90118) and the forced re-login after
 * the reset.
 */
const STORAGE_KEY = 'qct360.auth.redirects';
const WINDOW_MS = 60_000;
const MAX_REDIRECTS_PER_WINDOW = 3;
const AUTHORIZE_ENDPOINT = '/oauth2/v2.0/authorize';

export const LOGIN_FAILED_LOOP_REASON = 'loop';
export const LOGIN_FAILED_LOOP_URL = `/auth/login-failed?reason=${LOGIN_FAILED_LOOP_REASON}`;

/**
 * Records one redirect to B2C. Returns false when the redirect must not happen because too many
 * sign-ins were started within the window. Logout and other non-authorize navigations are never counted.
 */
export function registerAuthRedirect(url: string, now = Date.now()): boolean {
  if (!url.includes(AUTHORIZE_ENDPOINT)) {
    return true;
  }
  const recent = readTimestamps().filter((timestamp) => now - timestamp < WINDOW_MS);
  if (recent.length >= MAX_REDIRECTS_PER_WINDOW) {
    return false;
  }
  writeTimestamps([...recent, now]);
  return true;
}

/** Forgets the recorded redirects; called once a redirect came back with a token, so it was no loop. */
export function clearAuthRedirects(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: nothing to clear.
  }
}

function readTimestamps(): number[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((value): value is number => typeof value === 'number') : [];
  } catch {
    return [];
  }
}

function writeTimestamps(timestamps: number[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(timestamps));
  } catch {
    // Storage unavailable: never block a login because of it.
  }
}
